import { inject } from '@angular/core';
import { HttpErrorResponse, HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { BACKGROUND_HEALTH_CHECK, BackendStatusService } from '../backend-status/backend-status.service';
import { ERROR_REPORTER } from '../observability/error-reporter';
import { buildErrorReport, reportContext } from '../observability/error-report';
import { isNetworkFailure, type ApiError } from '../../models/api-error.model';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const reporter = inject(ERROR_REPORTER);
  const backendStatus = inject(BackendStatusService);

  return next(req).pipe(
    // Cualquier respuesta exitosa prueba que el backend está vivo: el estado global se
    // actualiza pasivamente con el tráfico real, sin ningún polling mientras está arriba.
    tap((event) => {
      if (event instanceof HttpResponse) {
        backendStatus.markUp();
      }
    }),
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      const unreachable = isBackendUnreachable(error);
      if (unreachable) {
        backendStatus.markDown();
      } else if (hasContractBody(error)) {
        // Un error con el contrato {code,message,traceId} lo escribió el backend: está vivo.
        backendStatus.markUp();
      }

      const apiError = toApiError(error, unreachable);
      const isLoginRequest = req.url.endsWith('/auth/login');
      // /api/v1/public/** (pantalla "Pruebas") se autentica por X-API-Key propia, no por el JWT
      // de sesión del admin (ver jwt.interceptor.ts, mismo criterio /public/). Un 401 ahí es una
      // API key pública inválida/desincronizada, no una sesión de admin vencida: tratarlo como tal
      // deslogueaba a cualquier operador que solo visitara "Pruebas" (bug real de producción).
      const isPublicApiRequest = req.url.includes('/public/');

      // Solo se reportan los errores inesperados (5xx y fallos de red). Los 4xx son
      // flujos previstos (validación, sesión vencida, permisos) que ya maneja la UI.
      // Los pings de salud en segundo plano no se reportan: con el backend caído generarían
      // un reporte cada 15s sin información nueva (la caída ya se reportó con el request real).
      if (!req.context.get(BACKGROUND_HEALTH_CHECK) && (isNetworkFailure(apiError) || apiError.status >= 500)) {
        try {
          reporter.report(buildErrorReport(`${req.method} ${pathOf(req.url)}`, apiError, reportContext(router.url)));
        } catch {
          // El reporte nunca debe reemplazar al ApiError normalizado que esperan los suscriptores.
        }
      }

      if (apiError.status === 401 && !isLoginRequest && !isPublicApiRequest) {
        const returnUrl = router.url;
        auth.logout();
        // Si varios requests fallan con 401 a la vez, el primero navega y los demás repiten la
        // misma navegación (idempotente) o, si ya estamos en /login, no navegan de nuevo: así no
        // se pisa el returnUrl original con "/login". Acá tampoco se emite ningún toast: el
        // aviso de sesión expirada lo muestra la pantalla de login vía reason=expired.
        if (!returnUrl.startsWith('/login')) {
          void router.navigate(['/login'], { queryParams: { returnUrl, reason: 'expired' } });
        }
      }

      return throwError(() => apiError);
    }),
  );
};

/** Path del request sin origin ni query string (la query puede llevar filtros con datos personales). */
function pathOf(url: string): string {
  try {
    return new URL(url, 'http://relative').pathname;
  } catch {
    return url.split('?')[0];
  }
}

/**
 * "El backend nunca contestó": status 0 (red caída, CORS, DNS, timeout reformulado) o un 5xx
 * cuyo body NO es el contrato de error del backend. Este segundo caso apareció con el dev proxy
 * (proxy.conf.mjs): con el backend caído, el dev server contesta él mismo 500 con body text/html
 * — y nginx en producción hace lo mismo (502/504 con página HTML). Un 5xx CON contrato, en
 * cambio, es el backend vivo reportando su propio error y se trata como tal.
 */
function isBackendUnreachable(error: HttpErrorResponse): boolean {
  // Alcance deliberado: solo status 0 y 5xx. Un 4xx de infraestructura sin contrato (p.ej. un
  // 404 HTML de nginx con el backend caído) no cambia el estado — no hay forma confiable de
  // distinguirlo de un 4xx legítimo, y el próximo request real lo resuelve.
  return error.status === 0 || (error.status >= 500 && !hasContractBody(error));
}

/**
 * El contrato de error del backend siempre trae `message` string ({code,message,traceId}):
 * verificado contra su GlobalExceptionHandler — todos los handlers, incluido el catch-all de
 * Exception, construyen el body uniforme vía error(status, code, message, ex). Un 5xx sin
 * `message` no puede venir de la app backend; solo del proxy/nginx/dev-server.
 */
function hasContractBody(error: HttpErrorResponse): boolean {
  const body = error.error as Partial<ApiError> | null;
  return typeof body?.message === 'string';
}

function toApiError(error: HttpErrorResponse, unreachable: boolean): ApiError {
  const body = error.error as Partial<ApiError> | null;
  const fieldErrors = toFieldErrors(body?.fieldErrors);
  return {
    status: error.status,
    code: body?.code ?? 'UNKNOWN_ERROR',
    // Auditoría de robustez 2026-07-06: en un fallo de red real (status 0, sin `body`), el
    // fallback caía en `error.message` — el texto crudo de Angular ("Http failure response for
    // http://.../admin/x: 0 Unknown Error"), técnico y en inglés, se mostraba tal cual al
    // operador en cualquier pantalla que use `serverErrorMessage()` sin chequear
    // `isNetworkFailure` por su cuenta (rule-set-form/form-config-form sí lo hacían; el resto no).
    // Centralizado acá para que ningún consumidor tenga que acordarse de hacerlo.
    // 2026-07-07: mismo mensaje para el 5xx sin contrato del proxy/dev-server/nginx — para el
    // operador es indistinguible de una caída de red (el backend nunca contestó).
    message: unreachable
      ? 'No se pudo conectar con el servidor. Verificá tu conexión e intentá de nuevo.'
      : (body?.message ?? error.message ?? 'Ocurrió un error inesperado.'),
    traceId: body?.traceId ?? '',
    ...(fieldErrors ? { fieldErrors } : {}),
  };
}

/** Acepta el mapa campo → mensaje solo si de verdad es un objeto de strings. */
function toFieldErrors(value: unknown): Record<string, string> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value);
  if (entries.length === 0 || entries.some(([, message]) => typeof message !== 'string')) {
    return null;
  }
  return value as Record<string, string>;
}
